// src/components/document/HeaderFooterEditor.tsx
// Inline editor for page headers and footers with 3 zones (left, center, right)
// and variable insertion support.

import { useState, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import { VariableInsertMenu } from './VariableInsertMenu';

export interface HeaderFooterZones {
  left: string;
  center: string;
  right: string;
}

interface HeaderFooterEditorProps {
  label: string;
  value: HeaderFooterZones;
  onChange: (value: HeaderFooterZones) => void;
  readOnly?: boolean;
}

export function HeaderFooterEditor({
  label,
  value,
  onChange,
  readOnly = false,
}: HeaderFooterEditorProps) {
  const { t } = useI18n();
  const [activeZone, setActiveZone] = useState<'left' | 'center' | 'right' | null>(null);

  const handleChange = useCallback(
    (zone: 'left' | 'center' | 'right', text: string) => {
      onChange({ ...value, [zone]: text });
    },
    [value, onChange],
  );

  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (!activeZone) return;
      const current = value[activeZone];
      onChange({ ...value, [activeZone]: current + variable });
    },
    [activeZone, value, onChange],
  );

  const zones: Array<{ key: 'left' | 'center' | 'right'; labelKey: string; align: string }> = [
    { key: 'left', labelKey: 'headerFooter.left', align: 'text-left' },
    { key: 'center', labelKey: 'headerFooter.center', align: 'text-center' },
    { key: 'right', labelKey: 'headerFooter.right', align: 'text-right' },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {!readOnly && <VariableInsertMenu onInsert={handleInsertVariable} />}
      </div>

      <div className="grid grid-cols-3 gap-2 p-2 rounded-lg border border-border bg-muted/20">
        {zones.map((zone) => (
          <div key={zone.key}>
            <label className="block text-[10px] text-muted-foreground mb-0.5 uppercase tracking-wider">
              {t(zone.labelKey)}
            </label>
            {readOnly ? (
              <div className={`text-xs text-muted-foreground font-mono min-h-[28px] px-2 py-1.5 ${zone.align}`}>
                {value[zone.key] || '--'}
              </div>
            ) : (
              <input
                type="text"
                value={value[zone.key]}
                onChange={(e) => handleChange(zone.key, e.target.value)}
                onFocus={() => setActiveZone(zone.key)}
                placeholder={t(zone.labelKey)}
                className={`w-full px-2 py-1.5 text-xs font-mono rounded border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 ${zone.align}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
